import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Rating,
  Chip,
  Alert,
  CircularProgress,
  Link,
  IconButton,
  Tooltip,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { Add as AddIcon, Visibility as EyeIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import apiClient from '../config/axios';
import dayjs from 'dayjs';
import ModalProposedGrade from '../components/ModalProposedGrade';
import ModalWall from '../components/ModalWall';
import ModalConfirm from '../components/ModalConfirm';
import ModalResult from '../components/ModalResult';
import { useAuth } from '../contexts/AuthContext';
import getConfig from '../config/app-config';

const Walls = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [walls, setWalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedWallId, setSelectedWallId] = useState(null);
  const [wallModalOpen, setWallModalOpen] = useState(false);
  const [editWallData, setEditWallData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [deleteWallId, setDeleteWallId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [resultModal, setResultModal] = useState({ open: false, message: '', severity: 'info' });
  const navigate = useNavigate();

  const fetchWalls = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/walls');
      setWalls(response.data);
    } catch (err) {
      console.error('Errore nel caricamento delle vie:', err);
      setError('Errore nel caricamento delle vie. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWalls();
  }, []);

  const handleAddGrade = (wallId) => {
    setSelectedWallId(wallId);
    setModalOpen(true);
  };

  const handleEditWall = (wall) => {
    setIsEditMode(true);
    setEditWallData(wall);
    setWallModalOpen(true);
  };

  const handleDeleteWall = (wall) => {
    setDeleteWallId(wall._id);
    setShowDeleteConfirm(true);
  };

  const hasUserProposedGrade = (wall) => {
    if (!user || !wall.proposedGrades) return false;
    return wall.proposedGrades.some(pg => pg.userId && pg.userId._id === user._id);
  };

  const allColumns = useMemo(() => [
    {
      field: 'name',
      headerName: 'Nome',
      // width: 100,
      flex: 1,
      renderCell: (params) => {
        const nameParts = params.value ? params.value.split(' ') : [];
        const firstPart = nameParts.slice(0, Math.ceil(nameParts.length / 2)).join(' ');
        const secondPart = nameParts.slice(Math.ceil(nameParts.length / 2)).join(' ');
        
        return (
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate(`/walls/${params.row._id}`)}
            sx={{ 
              textDecoration: 'none',
              color: 'primary.main',
              fontWeight: 'medium',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              lineHeight: 1.2,
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            <Box component="span">{firstPart}</Box>
            {secondPart && <Box component="span">{secondPart}</Box>}
          </Link>
        );
      },
    },
    {
      field: 'createdAt',
      headerName: 'Data',
      // width: 120,
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2">
          {dayjs(params.value).format('DD-MMM-YY')}
        </Typography>
      ),
    },
    {
      field: 'officialGrade',
      headerName: 'Grado',
      // width: 80,
      flex: 0.7,
      renderCell: (params) => {
        const showProposed = isMobile && params.row.averageGrade;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
            <Chip
              label={params.value}
              color="primary"
              variant="contained"
              size="small"
            />
            {showProposed && (
              <Chip
                label={`${params.row.averageGrade}`}
                color="secondary"
                variant="contained"
                size="small"
              />
            )}
          </Box>
        );
      },
    },
    {
      field: 'averageGrade',
      headerName: 'Proposto',
      // width: 100,
      flex: 0.7,
      renderCell: (params) => (
        <Chip
          label={params.value || '-'}
          color="secondary"
          variant="contained"
          size="small"
        />
      ),
    },
    {
      field: 'ascents',
      headerName: 'Reps',
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value || 0}
        </Typography>
      ),
    },
    {
      field: 'notes',
      headerName: 'Note',
      flex: 1,
      minWidth: 300,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxWidth: '100%',
          }}
        >
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'rating',
      headerName: 'Bellezza',
      // width: 130,
      flex: 1.15,
      renderCell: (params) => (
        <Rating
          value={params.value || 0}
          precision={0.1}
          readOnly
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      // width: 120,
      flex: (user?.roles.includes('admin') || user?.roles.includes('teacher')) ? 0.85 : 0.5,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Visualizza dettagli via">
              <IconButton
                onClick={() => navigate(`/walls/${params.row._id}`)}
                color="primary"
                size="small"
              >
                <EyeIcon />
              </IconButton>
            </Tooltip>
            {user && (
              <Tooltip title="Aggiungi grado proposto">
                <IconButton
                  onClick={() => handleAddGrade(params.row._id)}
                  color="primary"
                  size="small"
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {(user?.roles.includes('admin') || user?.roles.includes('teacher')) && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Modifica via">
                <IconButton
                  onClick={() => handleEditWall(params.row)}
                  color="primary"
                  size="small"
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Elimina via">
                <IconButton
                  onClick={() => handleDeleteWall(params.row)}
                  color="error"
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      ),
    },
  ], [navigate, user, isMobile]);

  // Colonne da nascondere su mobile: Data, Reps, Note, Proposto
  const columns = useMemo(() => {
    console.log('isMobile', isMobile);
    if (isMobile) {
      return allColumns.filter(
        col => !['createdAt', 'ascents', 'notes', 'averageGrade'].includes(col.field)
      );
    }
    return allColumns;
  }, [isMobile, allColumns]);

  const config = getConfig();
  // Verifica che sia Rock Dreams
  
  if (!config.WITH_WALLS) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Questa funzionalità è disponibile solo per palestre di arrampicata.
        </Alert>
      </Container>
    );
  }

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedWallId(null);
  };

  const handleGradeAdded = () => {
    // Ricarica i dati per aggiornare la media e il numero di ripetizioni
    fetchWalls();
  };

  const handleAddWall = () => {
    setIsEditMode(false);
    setEditWallData(null);
    setWallModalOpen(true);
  };

  const handleWallModalClose = () => {
    setWallModalOpen(false);
    setIsEditMode(false);
    setEditWallData(null);
  };

  const handleWallAdded = () => {
    // Ricarica i dati per mostrare la nuova via o aggiornare quella modificata
    fetchWalls();
  };

  const handleConfirmDelete = async () => {
    try {
      await apiClient.delete(`/api/walls/${deleteWallId}`);
      setShowDeleteConfirm(false);
      setDeleteWallId(null);
      // Ricarica i dati per aggiornare la lista
      fetchWalls();
      handleShowResult('Via eliminata con successo!', 'success');
    } catch (err) {
      console.error('Errore nella cancellazione della via:', err);
      setShowDeleteConfirm(false);
      setDeleteWallId(null);
      
      let errorMessage = 'Errore durante la cancellazione della via';
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }
      handleShowResult(errorMessage, 'error');
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteWallId(null);
  };

  const handleShowResult = (message, severity = 'info') => {
    setResultModal({ open: true, message, severity });
  };

  const handleCloseResult = () => {
    setResultModal({ open: false, message: '', severity: 'info' });
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  // console.log(user);
  // Verifica che i dati siano caricati e abbiano la struttura corretta
  const hasValidData = walls && Array.isArray(walls) && walls.length > 0;
  return (
    <Box sx={{ mt: 4, mb: 4, width: '100%' }}>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Vie di Arrampicata
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Esplora le vie di arrampicata disponibili con i loro gradi, valutazioni e note.
            </Typography>
          </Box>
          {(user?.roles.includes('admin') || user?.roles.includes('teacher')) && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddWall}
              sx={{ mt: 2, ml: 2 }}
            >
              Aggiungi Via
            </Button>
          )}
        </Box>
      </Box>

      {hasValidData ? (
        <Paper sx={{width: '100%'}}>
          <DataGrid
            disableColumnMenu
            // getRowHeight auto + 50px
            getRowHeight={() => isMobile ? 'auto' : 80} 
            rows={walls}
            columns={columns}
            getRowId={(row) => row._id}
            
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25, page: 0 },
              },
              sorting: {
                sortModel: [{ field: 'name', sort: 'asc' }],
              },
            }}
            rowsPerPageOptions={[25,50,100]}
            disableSelectionOnClick
            hideFooterPagination={walls.length <= 25}
            sx={{
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  verticalAlign: 'middle',
                  py: { xs: 1, md: 0 },
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#ffffff',
                },
                '& .MuiDataGrid-columnHeader': {
                  textAlign: 'left',
                  justifyContent: 'flex-start',
                },
                '& .MuiDataGrid-row.user-proposed-grade': {
                  backgroundColor: '#fff9c4',
                },
                '& .MuiDataGrid-cellContent': {
                  display: 'flex',
                  alignItems: 'center',
                  height: '100%',
                },
              }}
            getRowClassName={(params) => {
              return hasUserProposedGrade(params.row) ? 'user-proposed-grade' : '';
            }}
          />
        </Paper>
      ) : (
        <Paper sx={{ height: 600, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {loading ? 'Caricamento...' : 'Nessuna via di arrampicata disponibile'}
          </Typography>
        </Paper>
      )}

      <ModalProposedGrade
        open={modalOpen}
        onClose={handleModalClose}
        wallId={selectedWallId}
        onSuccess={handleGradeAdded}
      />

      <ModalWall
        open={wallModalOpen}
        onClose={handleWallModalClose}
        onSuccess={handleWallAdded}
        editData={editWallData}
        isEdit={isEditMode}
      />

      <ModalConfirm
        open={showDeleteConfirm}
        title="Conferma Eliminazione Via"
        message="Sei sicuro di voler eliminare questa via? Questa azione non può essere annullata."
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      <ModalResult
        open={resultModal.open}
        onClose={handleCloseResult}
        message={resultModal.message}
        severity={resultModal.severity}
        title={resultModal.severity === 'success' ? 'Successo' : 'Errore'}
      />
    </Box>
  );
};

export default Walls;
